

import { Component, Vue, Prop, InjectReactive } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"
import moment from "moment"

import vSelect from "vue-select"
import DataSourceSelect from "../Fields/DataSourceSelect.vue"
import DataSourceTemplate from "../Fields/DataSourceTemplate.vue"
import DateTime from "../Fields/DateTime.vue"
import Tags from "../Fields/Tags.vue"
import Numeric from "../Fields/Numeric.vue"
import UserRole from "./Users/UserRole.vue"
import FormGroup from "../Fields/FormGroup.vue"
import ExternalLink from "../Fields/ExternalLink.vue"
import UniqueCaseNumber from "../Fields/CaseNumber.vue"

import CaseFileUpload from "./Files/CaseFileUpload.vue"
import CaseFileDisplay from "./Files/CaseFileDisplay.vue"
import DataDetailsModal from "@/components/CaseDetails/DataDetailsModal.vue"
import MapLocation from "@/components/Fields/MapLocation.vue"

import { CaseDetails, Team, Field, CaseField, FieldInstance, CalculationType, CalculationDataTypeValue, DataType, Permission } from "@/types"

import CaseFile from "@/types/caseFile"
import { CurrentUser } from "@/types/user"

import api from "@/services/api"

import { LayoutLocations } from "@/types/templateLayout"
import TimeSpentDisplay from "./TimeSpentDisplay.vue"
import HubCaseService from "@/services/hubCaseService"
import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import { TenantSettingKeys } from "@/types/tenantSetting"
import { InUseBy } from "@/types/caseField"

import { TenantFeatures } from "@/types/tenantFeatures"
import { InProgressSaveDetails } from "@/types/inProgressSaveDetails"
import IconUpload from "@/components/IconUpload.vue"
import { DataObjectMethods, getPermissionTeams, hasEditPermission, hasViewPermission } from "@/scripts/caseFields"

const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"text-area-expand": TextAreaToggleExpand,
		"file-upload": CaseFileUpload,
		"case-file": CaseFileDisplay,
		"data-select": DataSourceSelect,
		"data-template": DataSourceTemplate,
		"tags": Tags,
		"data-details": DataDetailsModal,
		"form-group": FormGroup,
		"numeric": Numeric,
		"date-time": DateTime,
		"map-location": MapLocation,
		"user-role": UserRole,
		"external-link": ExternalLink,
		"case-number": UniqueCaseNumber,
		"time-spent-display": TimeSpentDisplay,
		"icon-upload": IconUpload
	}
})
export default class CaseFields extends Vue {
	$refs!: {
		viewItemDetails: DataDetailsModal;
	}

	@caseStore.Action("setFieldLockPolling")
	private setFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) => void;

	@caseStore.Action("cancelFieldLockPolling")
	private cancelFieldLockPolling!: ({ caseDetails, fieldId }: { caseDetails: CaseDetails, fieldId: string }) => void;

	@caseStore.Action("saveCaseField")
	private saveCaseField!: ({ caseField, inProgressUpdate }: { caseField: CaseField, inProgressUpdate: boolean }) => Promise<void>;

	@InjectReactive("hubCaseService") hubCaseService!: HubCaseService;

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getMyId")
	public myId!: string;

	@Getter("getTenantSettings")
	private tenantSettings!: { [ key: string]: any }

	@Getter("isFeatureEnabled")
	private isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	@caseStore.Getter("getSeverities")
	public severities!: any[];

	@Prop({ default: () => [] })
	public caseFiles!: CaseFile[];

	@caseStore.Getter("getPendingUploadFiles")
	pendingUploadFiles!: any[];

	@caseStore.Mutation
	cascadeListChange!: (args: { caseDetails: CaseDetails, caseField: CaseField, newValue: any, getFields: boolean }) => void

	@caseStore.Mutation
	setInProgressSaveDetails!: (details: InProgressSaveDetails) => void

	public dataSourceOptions: any[] = [];

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	@Prop({ default: null })
	public layout!: LayoutLocations | null;

	@Prop({ default: false })
	public canEditCurrentCase!: boolean

	@Prop()
	public currentCase!: CaseDetails;

	@Prop({ default: true })
	canUploadFiles!: boolean

	postInProgressDebounces: { [key: string]: NodeJS.Timeout } = {}
	savingFields: { [key: string]: boolean } = {}
	waitingFields: { [key: string]: boolean } = {}
	fieldErrors: { [key: string]: {
		error: string,
		clearTimeout: NodeJS.Timeout
	} } = {}

	private inProgressFieldId: string | undefined = "";
	private debounceInProgressUpdate: NodeJS.Timeout | null = null;

	CalculationType = CalculationType

	public get sections (): string[] {
		if (!this.currentCase || !this.currentCase.sections) {
			return []
		}

		if (this.currentCase.sectionDetails && this.layout !== null) {
			return this.currentCase.sections.filter(s => this.currentCase!.sectionDetails && this.currentCase!.sectionDetails[s]
				? this.layout === this.currentCase!.sectionDetails[s].layout
				: this.layout === LayoutLocations.Default)
		} else if (!this.currentCase.sectionDetails &&
			!(this.layout === LayoutLocations.Default || this.layout === null)) {
			return []
		}

		return this.currentCase.sections
	}

	public get viewableSections () {
		if (!this.currentCase || !this.currentCase.fields) {
			return this.sections
		}

		return this.sections.filter(section => this.caseFields[section] && this.caseFields[section].some(f => this.hasViewPermission(f)))
	}

	public get caseFields (): { [key:string]: CaseField[] } {
		if (!this.currentCase) {
			return {}
		}

		const allFields: CaseField[] = []

		if (this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				allFields.push(this.currentCase!.fields[fieldId])
			})
		}

		allFields.forEach((field: CaseField) => {
			if (this.currentCase!.sections) {
				const sectionOrder = this.currentCase!.sections.indexOf(field.section)
				field.sectionOrder = sectionOrder === -1 ? 99999 : sectionOrder
			}
		})

		const caseFields = allFields.sortByThen({ key: "sectionOrder" }, { thenKey: "order" })

		const dataSourceFields = caseFields.filter((field: any) => field.definitionId)
			.map((field: any) => field.definitionId)

		return caseFields.reduce((group: any, field: CaseField) => {
			var section = field.section ? field.section : "General";
			(group[section] = group[section] || []).push(field)
			return group
		}, {})
	}

	private get inProgressEnabled (): boolean {
		return !!this.currentCase.id && this.me && !!this.me.enabledFeatures && !!this.me.enabledFeatures.includes("inprogresssaving")
	}

	private get inProgressTextFieldUpdatesEnabled (): boolean {
		if (this.tenantSettings &&
			TenantSettingKeys.InProgressTextFieldSave in this.tenantSettings &&
			!!this.tenantSettings[TenantSettingKeys.InProgressTextFieldSave]) {
			return true
		}
		return false
	}

	private get currentCaseId (): string {
		return this.currentCase.id ?? ""
	}

	private async fieldGotFocus (caseField: CaseField, event: FocusEvent) {
		try {
			if (this.currentCase!.id && this.hasEditPermission(caseField)) {
				Vue.set(this.waitingFields, caseField.field.id!, true)
				const result = await api.selectCaseField(this.currentCase!.id, caseField.field.id!, true)
				this.setFieldLockPolling({ caseDetails: this.currentCase, fieldId: caseField.field.id! })

				if (this.isFeatureEnabled(TenantFeatures.AllowLockedFieldsToBeEdited) && !result.success) {
					this.fieldSetError(caseField.field.id!, `Field is also in use by ${caseField.inUseBy?.name}, changes may be overwritten`)
				} else if (!result.success) {
					window.blur()

					this.fieldSetError(caseField.field.id!, `Unable to edit ${caseField.field.title} - In use by ${result.inUseByName}`)
				}
			}
		} catch (ex) {
			this.fieldSetError(caseField.field.id!, `Unable to edit ${caseField.field.title} - ${ex}`)

			window.blur()
			console.error(ex)
		} finally {
			Vue.delete(this.waitingFields, caseField.field.id!)
		}
	}

	private isFieldReadOnly (caseField: CaseField): boolean {
		if ((caseField.inUseBy != null && caseField.inUseBy.id !== this.myId) && !this.isFeatureEnabled(TenantFeatures.AllowLockedFieldsToBeEdited)) {
			return true
		} else if (!this.hasEditPermission(caseField)) {
			return true
		}
		return false
	}

	private async fieldLostFocus (caseField: CaseField, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.hasEditPermission(caseField)) {
			if (caseField.oldValue !== newValue) {
				await this.savefieldChange(caseField, newValue)
			} else {
				await api.selectCaseField(this.currentCase!.id, caseField.field.id!, false)
			}
		}

		if (this.savingFields[caseField.field!.id!]) {
			Vue.delete(this.savingFields, caseField.field!.id!)
		}
		this.cancelFieldLockPolling({ caseDetails: this.currentCase, fieldId: caseField.field!.id! })
	}

	private fieldSetError (fieldId: string, error: string) {
		if (this.fieldErrors[fieldId]) {
			clearTimeout(this.fieldErrors[fieldId].clearTimeout)
		}

		Vue.set(this.fieldErrors, fieldId, {
			error,
			clearTimeout: setTimeout(() => {
				Vue.delete(this.fieldErrors, fieldId)
			}, 5000)
		})
	}

	private async dataListItemChanged (caseField: CaseField, newValue: any, getFields: boolean = false) {
		if (!this.currentCase.id) {
			caseField.label = newValue ? newValue.label : null
		}

		if (caseField.field.dataType !== "list" && caseField.field.dataType !== "list-multiple") return

		this.cascadeListChange({
			caseDetails: this.currentCase,
			caseField,
			newValue,
			getFields: false
		})
	}

	private async dateTimeFieldLostFocus (caseField: CaseField, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.hasEditPermission(caseField)) {
			if ((caseField.oldValue == null && newValue !== null) || (caseField.oldValue && moment(caseField.oldValue!).isSame(newValue) === false)) {
				await this.savefieldChange(caseField, newValue)
			} else {
				await api.selectCaseField(this.currentCase!.id, caseField.field.id!, false)
			}
		}
	}

	private async savefieldChange (caseField: CaseField, newValue: string, inProgressUpdate: boolean = false): Promise<void> {
		Vue.set(this.savingFields, caseField.field!.id!, true)

		try {
			caseField.value = newValue
			var inUseBy: InUseBy | null | undefined = null
			if (inProgressUpdate && caseField.inUseBy) {
				inUseBy = { ...caseField.inUseBy }
			}
			await this.saveCaseField({ caseField, inProgressUpdate })
			caseField.oldValue = newValue
			caseField.inUseBy = inUseBy
		} catch (err) {
			console.error(err)
			this.fieldSetError(caseField.field.id!, "Save failed")
		} finally {
			Vue.delete(this.savingFields, caseField.field!.id!)
		}
	}

	private async addItem (caseField: CaseField, newValue: any): Promise<void> {
		if (!this.hasEditPermission(caseField)) {
			return
		}
		return DataObjectMethods.addItem(caseField, newValue, this.currentCase)
	}
	private async updateItem (caseField: CaseField, updatedValue: any): Promise<void> {
		if (!this.hasEditPermission(caseField)) {
			return
		}
		return DataObjectMethods.updateItem(caseField, updatedValue, this.currentCase)
	}
	private async removeItem (caseField: CaseField, newValue: any): Promise<void> {
		if (!this.hasEditPermission(caseField)) {
			return
		}
		return DataObjectMethods.removeItem(caseField, newValue, this.currentCase)
	}

	private hasViewPermission (caseField: CaseField): boolean {
		return hasViewPermission(caseField, this.me, this.currentCase)
	}

	private hasEditPermission (caseField: CaseField): boolean {
		return hasEditPermission(caseField, this.currentCase, this.canEditCurrentCase, this.me)
	}

	private fieldFiles (fieldId: string): CaseFile[] {
		return this.caseFiles.filter(caseFile => caseFile.fieldId === fieldId)
	}

	private pendingUploadFieldFiles (fieldId: string): CaseFile[] {
		return this.pendingUploadFiles.filter(caseFile => caseFile.fieldId === fieldId)
	}

	private displayEditTeams (field: CaseField): string {
		const teams = getPermissionTeams(field, "edit", this.currentCase)
		if (!teams || teams.length === 0) {
			return ""
		}

		return teams.map(team => team.title).join(", ")
	}

	private openNewItemModal (openArgs: any) {
		this.$refs.viewItemDetails.open(openArgs)
	}

	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) {
			return false
		}

		if (field.noViewAccess) {
			return false
		}

		if ((field.value != null && field.value !== undefined && field.value !== false && field.value !== "") || (Array.isArray(field.value) && field.value.length === 0)) {
			return false
		}

		return true
	}

	private sectionDescription (section: string) {
		if (!this.currentCase || !this.currentCase.sectionDetails || !this.currentCase.sectionDetails[section]) {
			return ""
		}

		return this.currentCase.sectionDetails[section].description
	}

	private setFieldCalculated (fieldInstance: FieldInstance, calculated: boolean) {
		const field = fieldInstance.field
		if (this.currentCase && this.currentCase.id) {
			api.setFieldCalculated(this.currentCase.id, field.id!, calculated)
		} else {
			if (calculated) {
				field.dataType = "calculated"
				fieldInstance.value = null
			} else if (field.dataType === "calculated") {
				const dataType = CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
				switch (dataType) {
					case DataType.string:
						field.dataType = "text"
						break
					case DataType.dateTime:
						field.dataType = "date"
						break
					case DataType.number:
						field.dataType = "number"
						break
					case DataType.boolean:
						field.dataType = "checkbox"
						break
				}
			}
		}
	}

	private isCheckboxCalculation (field: Field): boolean {
		return !!field.extensions &&
			CalculationDataTypeValue[field.extensions.calculationType as CalculationType] === DataType.boolean
	}

	private uploadFile (fieldAndFiles: { fieldId: string, uploadFiles: any[] }) {
		this.$emit("uploadFiles", fieldAndFiles)
	}

	private updateInProgress (fieldId: string, value: string) {
		if (!this.inProgressEnabled) return

		if (this.postInProgressDebounces[fieldId]) {
			clearTimeout(this.postInProgressDebounces[fieldId])
		}

		this.postInProgressDebounces[fieldId] = setTimeout(async () => {
			Vue.set(this.savingFields, fieldId, true)

			clearTimeout(this.postInProgressDebounces[fieldId])
			delete this.postInProgressDebounces[fieldId]

			await this.hubCaseService.inProgressUpdate(this.currentCase!.id!, fieldId, value)
			Vue.delete(this.savingFields, fieldId)
		}, 1000)
	}

	private async inProgressTextFieldUpdate (caseField: CaseField, newValue: string): Promise<void> {
		if (!this.inProgressTextFieldUpdatesEnabled || this.inProgressFieldId === caseField?.field?.id || (newValue && newValue.length < 10)) {
			return
		}

		if (this.debounceInProgressUpdate != null) {
			clearTimeout(this.debounceInProgressUpdate)
			this.debounceInProgressUpdate = null
		}

		try {
			this.inProgressFieldId = caseField?.field?.id
			if (caseField && newValue && caseField.oldValue !== newValue && this.currentCase!.id && this.hasEditPermission(caseField)) {
				if (this.inProgressFieldId) {
					this.setInProgressSaveDetails({ caseId: this.currentCase!.id, fieldId: this.inProgressFieldId, text: newValue })
				}
				await this.savefieldChange(caseField, newValue, true)
			}
		} catch (ex) {
			console.error(ex)
		}

		this.debounceInProgressUpdate = setTimeout(() => { this.inProgressFieldId = "" }, 15000)
	}
}
